import type { FileData } from 'src/data-capture/hooks/file-uploads/types';

export enum TranscriptionStatus {
  READY = 'READY',
  PROCESSING = 'PROCESSING',
  FAILED = 'FAILED',
  EMPTY = 'EMPTY',
}

export type TranscriptSegment = {
  speaker: string;
  // Stable diarisation label the backend keys speaker corrections on, e.g.
  // "SPEAKER_0". Null on transcripts that predate diarisation capture, where the
  // display `speaker` is itself the label to correct against.
  diarisationLabel?: string | null;
  content: string;
  startTime: string;
  endTime: string;
};

// A name Siesta suggests for an unidentified speaker, with the client or colleague it names, if any.
export type SpeakerSuggestion = {
  name: string;
  clientId: string | null;
  userId: string | null;
  // The name the meeting calls this voice, when that is not the suggested person's.
  heardAs?: string | null;
  // The suggested client or colleague already speaks for another voice here, so this one was
  // probably split off from them.
  alreadySpeaks?: boolean;
};

export type Transcript = {
  status: TranscriptionStatus;
  id?: string;
  eventId?: string;
  file?: FileData;
  segments?: TranscriptSegment[];
  summary?: string;
  summaryStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'ERROR' | 'COMPLETED';
  dataUploadFileId?: string;
  // The client endpoints only serve a file to the client who owns it, so the recording is
  // read through its owner rather than through whichever client attends first.
  recordingOwnerClientId?: string | null;
  // Keyed by the voice's diarisation label, as segments carry it in diarisationLabel.
  speakerSuggestions?: Record<string, SpeakerSuggestion>;
  // Diarisation labels of voices too short to be anyone on their own. Absent while the speaker pipeline is off.
  speakerFragments?: string[];
};
